// Scherm 6 — de skill-lijst, met de grafiekkaarten van scherm 7 erboven en het
// detailpaneel van scherm 8 ernaast.
//
// Vorm uit de dummy (V1 r. 1670-1957): filterkaart, weergaveknoppen, sorteerrij,
// tabelkaart met kopregel van 34px, paginering, detailpaneel van rechts.
// Tabelkolommen letterlijk uit V1 r. 1909.
//
// Twee dingen die ik NIET uit de dummy overneem, allebei omdat ze iets
// verbergen:
//  1. V1 filtert stilzwijgend álle Declining-skills weg zodra de quick-win-pil
//     uit staat (r. 1559). Dat zijn er hier 26. Status is hier een gewoon
//     filtermenu mét aantallen — wat je niet ziet, zie je omdat je het hebt
//     weggeklikt.
//  2. De SummaPick-badge. `summaRecommended` staat op 0 van de 1.762: die vlag
//     wordt door het beheerscherm gezet en nooit door de pijplijn (W8 B5).
//     Geen badge zonder grond.
//
// De lijst NEST zoals de trendlijst (owner-besluit, planreview 2026-08-24):
// 823 van de 1.762 skills hangen onder een ouder-skill. Dezelfde telregel als
// ADR §7 — elk getal op dit scherm telt de regels van deze weergave.

import { useEffect, useState } from "react";

import { EmptyList, LoadError } from "../components/Empty";
import { FilterChips, type Chip } from "../components/FilterChips";
import { FilterMenu } from "../components/FilterMenu";
import { OnderwerpFilter } from "../components/OnderwerpFilter";
import { SkillCharts } from "../components/SkillCharts";
import { SkillDetail } from "../components/SkillDetail";
import { SourceDetail } from "../components/SourceDetail";
import {
  Card,
  FilterBar,
  PageHeader,
  Pager,
  ScoreCell,
  SearchField,
  SlidePanel,
  TableHead,
} from "../components/ui";
import type { Skill, SkillDetailResponse } from "../contracts";
import { api } from "../lib/api";
import { useMeta } from "../lib/meta";
import { bedrijfstakSectoren, gemetenZin, usePropositieFilter } from "../lib/propositieFilter";
import { useDebounced, useResource } from "../lib/useResource";
import { alsLijst, alsTekst, alsVlag, type Voorzet } from "../lib/voorzet";
import { color, radius, size, weight } from "../tokens";

const COLS = "1fr 150px 78px 72px 72px 72px 48px";
const LABELS = ["Vaardigheid", "Categorie", "Prioriteit", "Impact", "Trending", "Urgentie", "Bron"] as const;
const PAGE_SIZE = 15;

const TYPE_OPTIONS = ["Hard Skill", "Soft Skill", "Methodology", "Tool"] as const;

// De statuswoorden in gewone taal; de API-waarden blijven de contract-enum.
const STATUS_OPTIONS = ["Opkomend", "Gevestigd", "Afnemend"] as const;
const STATUS_PARAM: Record<string, string> = {
  Opkomend: "Emerging",
  Gevestigd: "Established",
  Afnemend: "Declining",
};

const HORIZON_OPTIONS = ["< 6 months", "6–12 months", "1–2 years", "2+ years"] as const;

// De banden uit de dummy (V1 r. 1549-1550), met de grenzen zoals de API ze
// rekent: ondergrens erbij, bovengrens eraf, zodat een score met een decimaal
// nooit tussen wal en schip valt.
const IMPACT_OPTIONS = ["Zeer hoog (9+)", "Hoog (7-9)", "Midden (5-7)", "Laag (<5)"] as const;
const IMPACT_PARAM: Record<string, string> = {
  "Zeer hoog (9+)": "zeer_hoog",
  "Hoog (7-9)": "hoog",
  "Midden (5-7)": "midden",
  "Laag (<5)": "laag",
};
const EFFORT_OPTIONS = ["Zeer laag (<3)", "Laag (3-5)", "Midden (5-7)", "Hoog (7+)"] as const;
const EFFORT_PARAM: Record<string, string> = {
  "Zeer laag (<3)": "zeer_laag",
  "Laag (3-5)": "laag",
  "Midden (5-7)": "midden",
  "Hoog (7+)": "hoog",
};

const SORTS: { key: string; label: string; hint: string }[] = [
  {
    key: "prioriteit",
    label: "Prioriteit",
    hint: "Bewijskracht: vraag uit de trends, breedte van het bewijs en de investering samen.",
  },
  { key: "trending", label: "Trending", hint: "Hoe hard het signaal groeit." },
  { key: "urgentie", label: "Urgentie", hint: "Hoe snel het speelt." },
];

/** De drie weergaven uit de dummy (V1 r. 1757-1760). */
type View = "highlights" | "grid" | "tabel";
const VIEWS: { key: View; label: string; hint: string }[] = [
  {
    key: "highlights",
    label: "✦ Kaarten",
    hint: "Grote kaarten met de scores erbij — negen per pagina, zoals de referentie.",
  },
  { key: "grid", label: "⊞ Compact", hint: "Kleine kaarten, meer per scherm." },
  {
    key: "tabel",
    label: "≡ Tabel",
    hint: "De volledige lijst, met de nesting van bundels en varianten.",
  },
];

type DetailLevel = "super" | "kern";
const DETAIL_LEVELS: { key: DetailLevel; label: string; hint: string }[] = [
  {
    key: "super",
    label: "Bundels",
    hint: "Alleen vaardigheden die andere vaardigheden bundelen. De rest zit eronder.",
  },
  {
    key: "kern",
    label: "Alle vaardigheden",
    hint: "Elke vaardigheid als eigen regel; de smallere varianten ingeklapt.",
  },
];

const BADGE_LABEL: Record<string, string> = {
  trending: "snelle stijger",
  quickwin: "quick win",
  urgent: "urgent",
  priority: "topprioriteit",
};

/**
 * De uitleg bij de combinatie die de owner op 2026-08-24 aanwees: een kaart die
 * tegelijk "snelle stijger" en "Afnemend" draagt.
 *
 * Dat is geen fout in de data. De badge meet hoe hard het SIGNAAL groeit
 * (trending-score), de status of de VRAAG krimpt — dus een snel groeiend
 * signaal *over* een krimp. Gemeten: precies één van de 1.762 vaardigheden zit
 * in die situatie. Zonder deze zin leest hij als een tegenstrijdigheid.
 */
const STIJGER_EN_KRIMP =
  "Allebei tegelijk, en dat kan: 'snelle stijger' zegt hoe hard het SIGNAAL " +
  "groeit, 'Afnemend' dat de VRAAG krimpt. Er wordt hier dus druk geschreven " +
  "over het verdwijnen van dit werk.";

function Row({
  skill,
  indent,
  onOpen,
}: {
  skill: Skill;
  indent: boolean;
  onOpen: (s: Skill) => void;
}) {
  const [hover, setHover] = useState(false);
  // W8-regel 2 wil nooit een kale score zonder reden. De owner wil de rij
  // schoon (opname 2026-08-24), dus de reden hangt aan de prioriteit en staat
  // voluit in het detailpaneel — verplaatst, niet geschrapt.
  const reden = skill.reason
    ? `Uitgelicht als ${BADGE_LABEL[skill.reason] ?? skill.reason}`
    : undefined;

  return (
    <div
      onClick={() => onOpen(skill)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "grid",
        gridTemplateColumns: indent ? `24px ${COLS}` : COLS,
        gap: 0,
        alignItems: "center",
        padding: "0 16px",
        minHeight: 44,
        cursor: "pointer",
        background: hover ? color.surfaceAlt : color.surface,
        transition: "background 0.1s",
        borderBottom: `1px solid ${color.lineFaint}`,
      }}
    >
      {indent && (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ opacity: 0.2 }}>
            <path
              d="M3 1v6a2 2 0 002 2h4"
              stroke={color.faint}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0, paddingRight: 8 }}>
        <span
          title={
            skill.status === "Declining"
              ? "Afnemend: bronnen beschrijven krimp in de vraag."
              : skill.status === "Emerging"
                ? "Opkomend: nieuw of snel groeiend."
                : "Gevestigd: al breed gevraagd."
          }
          style={{
            width: 6,
            height: 6,
            borderRadius: 99,
            flexShrink: 0,
            background:
              skill.status === "Declining"
                ? color.accentAmber
                : skill.status === "Emerging"
                  ? color.accentBlue
                  : color.disabled,
          }}
        />
        <span
          style={{
            fontSize: size.meta,
            fontWeight: indent ? 400 : weight.regular,
            color: color.ink,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {skill.displayTitle || skill.name}
        </span>
        {/* Krimp is een BEVINDING, geen voetnoot (owner-review 2026-08-24): dat
            werk verdwijnt is nieuws. Een stip van 6px zegt dat niet hard genoeg,
            dus krijgt hij dezelfde badge-vorm als quick win. Gemeten: 26. */}
        {skill.status === "Declining" && (
          <span
            title={
              skill.reason === "trending"
                ? STIJGER_EN_KRIMP
                : "Afnemend: bronnen beschrijven krimp in de vraag naar dit werk."
            }
            style={{
              fontSize: 8,
              fontWeight: weight.bold,
              padding: "1px 4px",
              borderRadius: 3,
              background: color.accentAmberBg,
              color: color.accentAmber,
              flexShrink: 0,
            }}
          >
            ↓ AFNEMEND
          </span>
        )}
        {skill.quickWin && (
          <span
            title="Veel impact voor weinig investering (impact ≥ 8, effort ≤ 6)."
            style={{
              fontSize: 8,
              fontWeight: weight.bold,
              padding: "1px 4px",
              borderRadius: 3,
              background: color.accentAmberBg,
              color: color.accentAmber,
              flexShrink: 0,
            }}
          >
            QUICK WIN
          </span>
        )}
      </div>
      <span
        title={
          skill.categoryProposed
            ? `Voorgestelde categorie, nog niet vastgesteld: ${skill.categoryProposed}`
            : skill.category
        }
        style={{
          fontSize: 11,
          color: skill.categoryProposed ? color.accentAmber : color.soft,
          fontStyle: skill.categoryProposed ? "italic" : "normal",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          paddingRight: 10,
        }}
      >
        {skill.category ?? skill.categoryProposed ?? "—"}
      </span>
      <ScoreCell value={skill.scores?.priority} tone={color.ink} title={reden} />
      <ScoreCell value={skill.scores?.impact} tone={color.accentGreen} />
      <ScoreCell value={skill.scores?.trending} tone={color.accentBlue} />
      <ScoreCell value={skill.scores?.urgency} tone={color.accentAmber} />
      <span style={{ fontSize: 11, color: color.soft, textAlign: "center" }}>
        {skill.sourceCount}
      </span>
    </div>
  );
}

/** De inklapbare groepskop, exact de vorm van de trendlijst. */
function Group({
  skill,
  items,
  onOpen,
  expanded,
}: {
  skill: Skill;
  items: Skill[];
  onOpen: (s: Skill) => void;
  expanded: boolean;
}) {
  const [open, setOpen] = useState(expanded);
  useEffect(() => setOpen(expanded), [expanded]);

  if (items.length === 0) {
    return <Row skill={skill} indent={false} onOpen={onOpen} />;
  }
  return (
    <div>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "0 16px",
          height: 38,
          cursor: "pointer",
          background: color.surfaceQuote,
          borderBottom: `1px solid ${color.lineSoft}`,
          borderLeft: `3px solid ${color.ink}`,
        }}
      >
        <svg
          width="9"
          height="9"
          viewBox="0 0 8 8"
          fill="none"
          style={{
            transition: "transform 0.15s",
            transform: open ? "rotate(90deg)" : "none",
            opacity: 0.45,
            flexShrink: 0,
          }}
        >
          <path
            d="M2 0l4 4-4 4"
            stroke={color.body}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span
          style={{
            fontSize: size.meta,
            fontWeight: weight.bold,
            color: color.ink,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {skill.displayTitle || skill.name}
        </span>
        <span
          title="Deze vaardigheid plus de smallere varianten eronder."
          style={{
            fontSize: 10,
            color: color.faint,
            background: color.line,
            padding: "1px 7px",
            borderRadius: 8,
            flexShrink: 0,
          }}
        >
          {1 + items.length}
        </span>
      </div>
      {open && (
        <>
          <Row skill={skill} indent={false} onOpen={onOpen} />
          {items.map((kind) => (
            <Row key={kind.id} skill={kind} indent onOpen={onOpen} />
          ))}
        </>
      )}
    </div>
  );
}

/**
 * De kaartweergaven uit de dummy (owner-review ronde 4, Skills #3: "naast de
 * tabelview ook een card view — highlight view in de referentie").
 *
 * De opmaak stond al ongebruikt in styles.css: `.h-card` (V1 r. 1588) en
 * `.g-card` (V1 r. 1596) zijn bij de overzetting meegekomen zonder dat er ooit
 * een kaart in stond. Dit is dus het invullen van een vorm die er al is, niet
 * een tweede ontwerptaal.
 *
 * ÉÉN EERLIJK VERSCHIL MET DE TABEL: een kaart kan niet nesten. In de tabel
 * hangen 823 van de 1.762 vaardigheden onder een ouder en tellen ze onder de
 * chevron mee; op kaarten staat elke vaardigheid als eigen kaart. Het scherm
 * zegt dat erbij in plaats van twee tellingen naast elkaar te zetten die niet
 * met elkaar te rijmen zijn.
 */

const STATUS_LABEL: Record<string, string> = {
  Emerging: "Opkomend",
  Established: "Gevestigd",
  Declining: "Afnemend",
};

const STATUS_TOELICHTING: Record<string, string> = {
  Emerging: "Opkomend: nieuw of snel groeiend.",
  Established: "Gevestigd: al breed gevraagd.",
  Declining: "Afnemend: bronnen beschrijven krimp in de vraag.",
};


/** Klein grijs pilletje met uitleg bij hover — de tags op de V1-kaart. */
function Pil({
  tekst,
  uitleg,
  toon = "grijs",
}: {
  tekst: string;
  uitleg: string;
  /** "amber" = een bevinding die op moet vallen (krimp), niet een etiket. */
  toon?: "grijs" | "amber";
}) {
  const amber = toon === "amber";
  return (
    <span
      title={uitleg}
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: 11,
        fontWeight: weight.medium,
        color: amber ? color.accentAmber : color.faint,
        background: amber ? color.accentAmberBg : color.surfaceQuote,
        border: `1px solid ${amber ? "#fde68a" : color.lineSoft}`,
        padding: "3px 9px",
        borderRadius: 6,
        whiteSpace: "nowrap",
        cursor: "default",
      }}
    >
      {tekst}
    </span>
  );
}

/** Eén scorebalk op een kaart (V1 r. 1846-1856). */
function KaartScore({ label, waarde }: { label: string; waarde: number | undefined }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, width: "100%" }}>
      <span style={{ fontSize: 12, color: color.faint, width: 52, flexShrink: 0 }}>{label}</span>
      <div
        style={{
          flex: 1,
          height: 3,
          background: color.lineSoft,
          borderRadius: 99,
          overflow: "hidden",
        }}
      >
        <div
          className="score-bar-fill"
          style={{
            height: "100%",
            width: `${Math.max(0, Math.min(10, waarde ?? 0)) * 10}%`,
            background: color.ink,
            borderRadius: 99,
            transformOrigin: "left",
            transition: "all 0.4s cubic-bezier(0.34,1.56,0.64,1)",
          }}
        />
      </div>
      <span
        style={{
          fontSize: 13,
          fontWeight: weight.medium,
          color: waarde === undefined ? color.disabled : color.ink,
          minWidth: 24,
          textAlign: "right",
        }}
      >
        {waarde === undefined ? "—" : waarde.toFixed(1)}
      </span>
    </div>
  );
}

/** De grote kaart (V1 `highlights`, r. 1775-1900). */
function HighlightCard({ skill, onOpen }: { skill: Skill; onOpen: (s: Skill) => void }) {
  // GEEN gedempte kleur meer bij Declining (owner-review 2026-08-24). De dummy
  // kleurde afnemende vaardigheden grijs én verborg ze standaard; grijs leest
  // als "uitgeschakeld", terwijl krimp juist een bevinding is — dat een
  // vaardigheid verdwijnt is nieuws, geen voetnoot. Ze staan er nu volwaardig
  // bij met een amber ↓-markering. Gemeten: 26 van de 1.762.
  const krimp = skill.status === "Declining";
  return (
    <div className="h-card" onClick={() => onOpen(skill)}>
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {skill.reason && (
          <Pil
            tekst={BADGE_LABEL[skill.reason] ?? skill.reason}
            uitleg="Waarom deze vaardigheid wordt uitgelicht."
          />
        )}
        <Pil
          tekst={(krimp ? "↓ " : "") + (STATUS_LABEL[skill.status] ?? skill.status)}
          uitleg={
            krimp && skill.reason === "trending"
              ? STIJGER_EN_KRIMP
              : (STATUS_TOELICHTING[skill.status] ?? "")
          }
          toon={krimp ? "amber" : "grijs"}
        />
        <Pil tekst={skill.type} uitleg="Soort vaardigheid." />
        {/* Niet twee keer hetzelfde: staat de vaardigheid al uitgelicht ALS
            quick win, dan zegt de reden-pil dat hierboven al. */}
        {skill.quickWin && skill.reason !== "quickwin" && (
          <Pil
            tekst="quick win"
            uitleg="Veel impact voor weinig investering (impact ≥ 8, effort ≤ 6)."
          />
        )}
      </div>
      <h3
        style={{
          fontSize: 17,
          fontWeight: weight.bold,
          color: color.ink,
          lineHeight: 1.3,
          marginBottom: 10,
          letterSpacing: "-0.015em",
        }}
      >
        {skill.displayTitle || skill.name}
      </h3>
      <p
        className="desc-clamp"
        style={{ fontSize: 14, color: color.faint, lineHeight: 1.6, marginBottom: 22 }}
      >
        {skill.description}
      </p>
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          paddingTop: 20,
          borderTop: `1px solid ${color.lineFaint}`,
        }}
      >
        <div style={{ width: "38%", flexShrink: 0, paddingRight: 16 }}>
          <div style={{ fontSize: 12, color: color.disabled, marginBottom: 6 }}>Prioriteit</div>
          <div
            style={{
              fontSize: 38,
              fontWeight: weight.bold,
              color: color.ink,
              letterSpacing: "-0.04em",
              lineHeight: 1,
            }}
          >
            {typeof skill.scores?.priority === "number"
              ? skill.scores.priority.toFixed(1)
              : "—"}
          </div>
        </div>
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 7,
            justifyContent: "center",
            borderLeft: `1px solid ${color.lineFaint}`,
            paddingLeft: 16,
            minWidth: 0,
          }}
        >
          <KaartScore label="Impact" waarde={skill.scores?.impact} />
          <KaartScore label="Urgentie" waarde={skill.scores?.urgency} />
          <KaartScore label="Trending" waarde={skill.scores?.trending} />
        </div>
      </div>
      <div style={{ marginTop: 20, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, color: color.faint }}>
          {skill.sourceCount} {skill.sourceCount === 1 ? "bron" : "bronnen"}
        </span>
        <span style={{ fontSize: 13, color: color.border }}>·</span>
        <span style={{ fontSize: 13, color: color.faint }}>
          {skill.trendCount} {skill.trendCount === 1 ? "trend" : "trends"}
        </span>
        <span style={{ fontSize: 13, color: color.border }}>·</span>
        <span style={{ fontSize: 13, color: color.faint }}>
          {skill.category ?? skill.sector}
        </span>
      </div>
    </div>
  );
}

/** De compacte kaart (V1 `grid`, r. 1905-1945). */
function GridCard({
  skill,
  rang,
  onOpen,
}: {
  skill: Skill;
  rang: number;
  onOpen: (s: Skill) => void;
}) {
  // Krimp valt op in plaats van weg te vallen — zie HighlightCard.
  const krimp = skill.status === "Declining";
  const kleur = krimp
    ? color.accentAmber
    : skill.status === "Emerging"
      ? color.accentGreen
      : color.accentBlue;
  return (
    <div className="g-card" onClick={() => onOpen(skill)}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
          gap: 8,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: weight.bold, color: color.border }}>
          #{rang}
        </span>
        <Pil tekst={skill.type} uitleg="Soort vaardigheid." />
      </div>
      <h4
        style={{
          fontSize: 15,
          fontWeight: weight.medium,
          color: color.ink,
          lineHeight: 1.35,
          marginBottom: 7,
        }}
      >
        {skill.displayTitle || skill.name}
      </h4>
      <p
        className="desc-clamp"
        style={{ fontSize: 13, color: color.faint, lineHeight: 1.55, marginBottom: 14 }}
      >
        {skill.description}
      </p>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontSize: 13,
          fontWeight: weight.medium,
          color: kleur,
          marginBottom: 12,
        }}
        title={
          krimp && skill.reason === "trending"
            ? STIJGER_EN_KRIMP
            : (STATUS_TOELICHTING[skill.status] ?? "")
        }
      >
        <span
          style={{ width: 7, height: 7, borderRadius: "50%", background: kleur, flexShrink: 0 }}
        />
        {(krimp ? "↓ " : "") + (STATUS_LABEL[skill.status] ?? skill.status)}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          paddingTop: 12,
          borderTop: `1px solid ${color.lineFaint}`,
        }}
      >
        <div>
          <div style={{ fontSize: 11, color: color.disabled, marginBottom: 3 }}>Prioriteit</div>
          <div
            style={{
              fontSize: 22,
              fontWeight: weight.bold,
              color: color.ink,
            }}
          >
            {typeof skill.scores?.priority === "number"
              ? skill.scores.priority.toFixed(1)
              : "—"}
          </div>
        </div>
        <span style={{ fontSize: 12, color: color.faint }}>
          {skill.sourceCount} {skill.sourceCount === 1 ? "bron" : "bronnen"}
        </span>
      </div>
    </div>
  );
}

/** Een treffer uit de zoekoverlay: die hoort hier open te gaan.
 *  Vóór owner-review ronde 4 werkte dat alleen voor trends; een skill of bron
 *  aanklikken deed niets. */
export function SkillsPage({
  openen,
  voorzet,
}: {
  openen?: Skill | null;
  /**
   * Een filter dat van buiten wordt voorgezet: de doorklik onder een
   * chatantwoord ("bekijk alle 43 op de Skills-tab") of een redactiekaart.
   * De waarden landen in dezelfde `useState`'s als de filtermenu's, zodat de
   * chips, "alles wissen" en de telling in de kop meteen kloppen — zou het
   * filter ernaast leven, dan toont het scherm iets anders dan het zegt.
   */
  voorzet?: Voorzet | null;
} = {}) {
  const meta = useMeta();
  const [page, setPage] = useState(1);
  const [sectors, setSectors] = useState<string[]>([]);
  // Filter Propositie (ADR 2026-10-23-ict-los-platform), zie lib/propositieFilter.
  const prop = usePropositieFilter(meta);
  const [types, setTypes] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [horizons, setHorizons] = useState<string[]>([]);
  const [impacts, setImpacts] = useState<string[]>([]);
  const [efforts, setEfforts] = useState<string[]>([]);
  const [onderwerpen, setOnderwerpen] = useState<string[]>([]);
  const [quickWin, setQuickWin] = useState(false);
  const [sorteer, setSorteer] = useState("prioriteit");
  const [detail, setDetail] = useState<DetailLevel>("kern");
  const [zoek, setZoek] = useState("");
  const [view, setView] = useState<View>("tabel");
  const [selected, setSelected] = useState<Skill | null>(null);
  const [detailData, setDetailData] = useState<SkillDetailResponse | null>(null);
  // De bron achter een bewijskaart, als tweede paneel over het eerste.
  const [bronId, setBronId] = useState<string | null>(null);
  // "Selectie uit de chat" (Chat 2.0): de knop onder het top-5-lijstje zet de
  // vijf hier voor als wisbare strook boven de lijst — de lijst zelf blijft
  // de gewone telbare weergave, dus de telregel van de kop blijft kloppen.
  const [selectie, setSelectie] = useState<{ id: string; titel: string }[] | null>(null);

  const sectorOptions = bedrijfstakSectoren(meta).map((s) => s.label);
  const sectorDisabled = Object.fromEntries(
    bedrijfstakSectoren(meta)
      .filter((s) => !s.active)
      .map((s) => [s.label, "Nog geen corpus: deze sector is voorbereid maar niet gemeten."]),
  );
  const sectorKey = (label: string | undefined) =>
    (meta?.sectors ?? []).find((s) => s.label === label)?.key;
  const categoryOptions = (meta?.categories ?? []).map((c) => c.name);

  // Wacht tot je even stil bent: anders vuurt élke toetsaanslag een ronde naar
  // de database, en daarvan zie je er maar één.
  const zoekTraag = useDebounced(zoek, 300);

  // Een treffer uit de zoekoverlay opent hier zijn detailpaneel.
  useEffect(() => {
    if (openen) setSelected(openen);
  }, [openen]);

  // Een voorgezet filter VERVANGT de huidige keuze en wist de rest: kwam je
  // hier via "bekijk alle 43", dan moet je die 43 zien en niet 43 doorsneden
  // met wat je een half uur geleden had aangeklikt.
  useEffect(() => {
    if (!voorzet) return;
    setCategories(alsLijst(voorzet, "category"));
    // Status staat op het scherm in het Nederlands en in de API als
    // Emerging|Established|Declining. Een voorzet komt van de API-kant en moet
    // dus terug vertaald worden, anders zet hij een label dat nergens op past.
    const naarLabel = Object.fromEntries(
      Object.entries(STATUS_PARAM).map(([label, param]) => [param, label]),
    );
    setStatuses(
      alsLijst(voorzet, "status")
        .map((p) => naarLabel[p] ?? p)
        .filter(Boolean) as string[],
    );
    setQuickWin(alsVlag(voorzet, "quickWin"));
    setZoek(alsTekst(voorzet, "zoek"));
    setSectors([]);
    setTypes([]);
    setHorizons([]);
    setImpacts([]);
    setEfforts([]);
    setOnderwerpen([]);
    setPage(1);
    // De chat kan een concrete selectie voorzetten ("Bekijk deze 5 op de
    // Skills-tab"): die landt als wisbare strook, niet als filter.
    const ruw = voorzet["selectie"];
    const rijen = Array.isArray(ruw)
      ? ruw.filter(
          (r): r is { id: string; titel: string } =>
            typeof r === "object" &&
            r !== null &&
            typeof (r as { id?: unknown }).id === "string" &&
            typeof (r as { titel?: unknown }).titel === "string",
        )
      : [];
    setSelectie(rijen.length > 0 ? rijen : null);
  }, [voorzet]);

  const filters = {
    sector: [...(sectors.map(sectorKey).filter(Boolean) as string[]), ...prop.sleutels],
    type: types,
    status: statuses.map((s) => STATUS_PARAM[s]).filter(Boolean) as string[],
    category: categories,
    horizon: horizons,
    impact: impacts.map((v) => IMPACT_PARAM[v]).filter(Boolean) as string[],
    effort: efforts.map((v) => EFFORT_PARAM[v]).filter(Boolean) as string[],
    quickWin: quickWin || undefined,
    topic: onderwerpen,
    search: zoekTraag || undefined,
  };
  const filterKey = [
    sectors.join("|"), prop.gekozen.join("|"), types.join("|"), statuses.join("|"), categories.join("|"),
    horizons.join("|"), impacts.join("|"), efforts.join("|"), String(quickWin),
    onderwerpen.join("|"), zoekTraag,
  ].join("¦");

  const list = useResource(
    () => api.skills({ ...filters, page, pageSize: PAGE_SIZE, sorteer, detail }),
    [page, filterKey, sorteer, detail, meta],
    meta !== null && prop.klaar,
  );
  // De grafieken hangen aan het filter, niet aan de pagina: bladeren laat ze staan.
  const charts = useResource(() => api.skillCharts(filters), [filterKey, meta], meta !== null && prop.klaar);
  const bron = useResource(() => api.source(bronId!), [bronId], bronId !== null);

  const facets = list.data?.facets;
  const countsFor = (axis: string) =>
    Object.fromEntries((facets?.[axis] ?? []).map((f) => [f.value, f.count]));
  const viaMap = (raw: Record<string, number>, labels: readonly string[],
                  param: Record<string, string>) =>
    Object.fromEntries(labels.map((label) => [label, raw[param[label]!] ?? 0]));

  const sectorCountsByKey = countsFor("sector");
  const sectorCounts = Object.fromEntries(
    (meta?.sectors ?? []).map((s) => [s.label, sectorCountsByKey[s.key] ?? 0]),
  );
  // Let op: dit telt REGELS (facetten), niet vaardigheden. Voor het filtermenu
  // is dat juist — daar wil je weten hoeveel regels een keuze oplevert. De
  // statusdonut telt vaardigheden en haalt zijn cijfers daarom uit /skills/charts.
  const statusCounts = countsFor("status");

  const chips: Chip[] = [
    ...prop.gekozen.map((v) => ({ axis: "Propositie", value: v, label: `Propositie: ${v}` })),
    ...sectors.map((v) => ({ axis: "Sector", value: v, label: v })),
    ...types.map((v) => ({ axis: "Type", value: v, label: v })),
    ...statuses.map((v) => ({ axis: "Status", value: v, label: v })),
    ...categories.map((v) => ({ axis: "Categorie", value: v, label: v })),
    ...horizons.map((v) => ({ axis: "Horizon", value: v, label: v })),
    ...impacts.map((v) => ({ axis: "Impact", value: v, label: `Impact: ${v}` })),
    ...efforts.map((v) => ({ axis: "Effort", value: v, label: `Effort: ${v}` })),
    ...onderwerpen.map((v) => ({ axis: "Onderwerp", value: v, label: `Onderwerp: ${v}` })),
    ...(quickWin ? [{ axis: "Quick wins", value: "qw", label: "Alleen quick wins" }] : []),
    ...(zoek ? [{ axis: "Zoek", value: zoek, label: `Zoekt op "${zoek}"` }] : []),
  ];
  const removeChip = (chip: Chip) => {
    const drop = (xs: string[]) => xs.filter((x) => x !== chip.value);
    if (chip.axis === "Propositie") prop.zet(drop(prop.gekozen));
    if (chip.axis === "Sector") setSectors(drop(sectors));
    if (chip.axis === "Type") setTypes(drop(types));
    if (chip.axis === "Status") setStatuses(drop(statuses));
    if (chip.axis === "Categorie") setCategories(drop(categories));
    if (chip.axis === "Horizon") setHorizons(drop(horizons));
    if (chip.axis === "Impact") setImpacts(drop(impacts));
    if (chip.axis === "Effort") setEfforts(drop(efforts));
    if (chip.axis === "Onderwerp") setOnderwerpen(drop(onderwerpen));
    if (chip.axis === "Quick wins") setQuickWin(false);
    if (chip.axis === "Zoek") setZoek("");
    setPage(1);
  };
  const clear = () => {
    setSectors([]);
    prop.terug();
    setTypes([]);
    setStatuses([]);
    setCategories([]);
    setHorizons([]);
    setImpacts([]);
    setEfforts([]);
    setOnderwerpen([]);
    setQuickWin(false);
    setZoek("");
    setPage(1);
  };

  const groups = list.data?.groups ?? [];
  // Voor de kaartweergaven: dezelfde vaardigheden als de tabel op deze pagina,
  // maar zonder nesting — ouder en varianten elk als eigen kaart.
  const plat = groups.flatMap((g) => [g.skill, ...g.children]);
  const kinderen = plat.length - groups.length;
  const total = list.data?.total ?? 0;
  // Hoeveel er ZELF aan het filter voldoen. Staat alleen in de kop als het
  // verschilt van `total` — dan hangen er varianten onder een treffer die zelf
  // niet matchen, en zonder dit getal is het filterresultaat niet na te tellen.
  const treffers = list.data?.totalMatching ?? total;
  const totalGroups = list.data?.totalGroups ?? groups.length;
  const totalPages = Math.max(1, Math.ceil(totalGroups / PAGE_SIZE));

  // Het detailpaneel haalt de trends erbij: de lijst draagt ze niet (de brug is
  // een aparte query en zou elke pagina duurder maken).
  useEffect(() => {
    if (!selected) {
      setDetailData(null);
      return;
    }
    let levend = true;
    setDetailData(null);
    api
      .skill(selected.id)
      .then((d) => levend && setDetailData(d))
      .catch(() => levend && setDetailData({ skill: selected, contributingTrends: [] }));
    return () => {
      levend = false;
    };
  }, [selected]);

  return (
    <div>
      <PageHeader
        title="Skills"
        count={
          list.loading
            ? "laden…"
            : `${total.toLocaleString("nl-NL")} ${total === 1 ? "vaardigheid" : "vaardigheden"} in beeld` +
              (treffers !== total
                ? ` · ${treffers.toLocaleString("nl-NL")} ${
                    treffers === 1 ? "voldoet" : "voldoen"
                  } aan het filter, de rest staat eronder als variant`
                : "") +
              (detail === "super"
                ? ` · gebundeld in ${totalGroups.toLocaleString("nl-NL")} ${
                    totalGroups === 1 ? "bundel" : "bundels"
                  }`
                : "") +
              ` · pagina ${page} van ${totalPages}`
        }
      />

      {selectie && (
        <Card style={{ marginBottom: 14, padding: "12px 18px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 8,
            }}
          >
            <span
              style={{
                fontSize: size.label,
                fontWeight: weight.medium,
                color: color.faint,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              Selectie uit de chat · {selectie.length}
            </span>
            <button
              onClick={() => setSelectie(null)}
              style={{
                marginLeft: "auto",
                background: "none",
                border: "none",
                padding: 0,
                font: "inherit",
                fontSize: 12,
                color: color.faint,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              selectie wissen
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {selectie.map((s, i) => (
              <button
                key={s.id}
                onClick={() => {
                  void api
                    .skill(s.id)
                    .then((d) => setSelected(d.skill))
                    .catch(() => undefined);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  background: "none",
                  border: "none",
                  borderTop: i === 0 ? "none" : `1px solid ${color.lineFaint}`,
                  padding: "8px 2px",
                  font: "inherit",
                  fontSize: size.meta,
                  color: color.ink,
                  fontWeight: weight.medium,
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    fontSize: 11.5,
                    color: color.faint,
                    width: 14,
                    textAlign: "right",
                    flexShrink: 0,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {i + 1}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>{s.titel}</span>
                <span style={{ color: color.disabled }}>→</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      <SkillCharts
        data={charts.data ?? null}
        loading={charts.loading}
        // Klik op de quick-wintegel → hetzelfde filter dat de badge gebruikt,
        // dus de lijst toont exact wat de tegel telt.
        onKiesKwadrant={() => {
          setQuickWin(true);
          setPage(1);
        }}
      />

      <FilterBar
        chips={<FilterChips chips={chips} onRemove={removeChip} onClearAll={clear} />}
      >
          {/* Zoek vóór Sector — dezelfde opening als Trends en Bronnen
              (owner-review ronde 4). De API kent `search` al; dit filtert de
              lijst zelf, en de grafiekkaarten bewegen mee. */}
          <SearchField
            value={zoek}
            onChange={(v) => {
              setZoek(v);
              setPage(1);
            }}
            placeholder="Zoek vaardigheden…"
          />
          <div style={{ width: 1, height: 24, background: color.border, flexShrink: 0 }} />
          {prop.toon && (
            <FilterMenu
              label="Propositie"
              options={prop.opties}
              values={prop.gekozen}
              note="Elk scherm opent op jouw propositie."
              onChange={(v) => {
                prop.zet(v);
                setPage(1);
              }}
            />
          )}
          <FilterMenu
            label="Sector"
            options={sectorOptions}
            values={sectors}
            counts={sectorCounts}
            disabled={sectorDisabled}
            note={
              `${gemetenZin(meta)}. De andere staan klaar in de configuratie en ` +
              "kleuren mee zodra hun corpus gedraaid is."
            }
            onChange={(v) => {
              setSectors(v);
              setPage(1);
            }}
          />
          <FilterMenu
            label="Categorie"
            options={categoryOptions}
            values={categories}
            counts={countsFor("category")}
            onChange={(v) => {
              setCategories(v);
              setPage(1);
            }}
          />
          <OnderwerpFilter
            onderwerpen={meta?.onderwerpen ?? []}
            gekozen={onderwerpen}
            facet={countsFor("topics")}
            eenheid="vaardigheden"
            onChange={(v) => {
              setOnderwerpen(v);
              setPage(1);
            }}
          />
          <FilterMenu
            label="Type"
            options={TYPE_OPTIONS}
            values={types}
            counts={countsFor("type")}
            onChange={(v) => {
              setTypes(v);
              setPage(1);
            }}
          />
          <FilterMenu
            label="Status"
            options={STATUS_OPTIONS}
            values={statuses}
            counts={viaMap(statusCounts, STATUS_OPTIONS, STATUS_PARAM)}
            onChange={(v) => {
              setStatuses(v);
              setPage(1);
            }}
          />
          <FilterMenu
            label="Impact"
            options={IMPACT_OPTIONS}
            values={impacts}
            counts={viaMap(countsFor("impact"), IMPACT_OPTIONS, IMPACT_PARAM)}
            onChange={(v) => {
              setImpacts(v);
              setPage(1);
            }}
          />
          <FilterMenu
            label="Effort"
            options={EFFORT_OPTIONS}
            values={efforts}
            counts={viaMap(countsFor("effort"), EFFORT_OPTIONS, EFFORT_PARAM)}
            onChange={(v) => {
              setEfforts(v);
              setPage(1);
            }}
          />
          <FilterMenu
            label="Horizon"
            options={HORIZON_OPTIONS}
            values={horizons}
            counts={countsFor("horizon")}
            note={
              "Bij 611 van de 1.766 vaardigheden staat geen horizon: de bronnen zeggen " +
              "niet wanneer. Die vallen buiten élke keuze hier."
            }
            onChange={(v) => {
              setHorizons(v);
              setPage(1);
            }}
          />
          <button
            className="filter-btn"
            title="Veel impact voor weinig investering (impact ≥ 8, effort ≤ 6)."
            onClick={() => {
              setQuickWin(!quickWin);
              setPage(1);
            }}
            style={{
              background: quickWin ? color.ink : color.surface,
              border: `1.5px solid ${quickWin ? color.ink : color.border}`,
              color: quickWin ? color.surface : color.body,
              padding: "9px 15px",
              borderRadius: radius.input,
              fontSize: size.lead,
              fontFamily: "inherit",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Quick wins
          </button>
      </FilterBar>

      {/* Weergave links, sorteren RECHTS — de indeling van de dummy (V1 r.
          1750-1765). Ze stonden allebei links tegen elkaar aan, waardoor
          "Bundels" en "Prioriteit" op één rij als één groep knoppen lazen. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 4,
            background: color.lineFaint,
            borderRadius: radius.input,
            padding: 3,
          }}
        >
          {VIEWS.map((v) => (
            <button
              key={v.key}
              className={"view-btn" + (view === v.key ? " active" : "")}
              title={v.hint}
              onClick={() => {
                setView(v.key);
                setPage(1);
              }}
            >
              {v.label}
            </button>
          ))}
        </div>
        {/* Nesting bestaat alleen in de tabel: een kaart kan geen kaart
            bevatten. In de kaartweergaven staat die keuze er dus niet, en
            zegt de regel eronder wat dat betekent voor de telling. */}
        {view === "tabel" && (
          <div
            style={{
              display: "flex",
              gap: 4,
              background: color.lineFaint,
              borderRadius: radius.input,
              padding: 3,
            }}
          >
            {DETAIL_LEVELS.map((l) => (
              <button
                key={l.key}
                className={"view-btn" + (detail === l.key ? " active" : "")}
                title={l.hint}
                onClick={() => {
                  setDetail(l.key);
                  setPage(1);
                }}
              >
                {l.label}
              </button>
            ))}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginLeft: "auto" }}>
          <span style={{ fontSize: size.meta, color: color.faint }}>Sorteren op</span>
          {SORTS.map((s) => (
            <button
              key={s.key}
              className={"sort-btn" + (sorteer === s.key ? " active" : "")}
              title={s.hint}
              onClick={() => {
                setSorteer(s.key);
                setPage(1);
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {list.error ? (
        <Card style={{ overflow: "hidden" }}>
          <LoadError reason={list.error} onRetry={list.reload} />
        </Card>
      ) : list.loading && !list.data ? (
        <Card style={{ overflow: "hidden" }}>
          <div style={{ padding: "48px 0", textAlign: "center", color: color.faint }}>laden…</div>
        </Card>
      ) : groups.length === 0 ? (
        <Card style={{ overflow: "hidden" }}>
          <EmptyList
            reason={
              chips.length > 0
                ? "Geen vaardigheden met deze filters."
                : "Geen vaardigheden gevonden — dit is een lege database, geen leeg filter."
            }
            onReset={chips.length > 0 ? clear : undefined}
          />
        </Card>
      ) : view === "tabel" ? (
        <Card
          style={{
            overflow: "hidden",
            // Blijft staan terwijl de nieuwe selectie onderweg is; dimt alleen.
            // Een lijst die bij elke filterklik leegloopt voelt trager dan hij is.
            opacity: list.stale ? 0.5 : 1,
            transition: "opacity 0.15s",
          }}
        >
          <TableHead cols={COLS} labels={LABELS} />
          {groups.map((group) => (
            <Group
              key={group.skill.id}
              skill={group.skill}
              items={group.children}
              onOpen={setSelected}
              // Filter je, dan wil je de treffers zien: dan staan de groepen open
              // en is het getal in de kop na te tellen op de pagina.
              expanded={chips.length > 0}
            />
          ))}
        </Card>
      ) : (
        <div style={{ opacity: list.stale ? 0.5 : 1, transition: "opacity 0.15s" }}>
          {kinderen > 0 && (
            <p style={{ fontSize: 12, color: color.faint, marginBottom: 10, lineHeight: 1.5 }}>
              Kaarten nesten niet: de {kinderen}{" "}
              {kinderen === 1 ? "variant" : "varianten"} die in de tabel onder een bundel
              staan, staan hier als eigen kaart. Het aantal kaarten is dus {plat.length}, niet{" "}
              {groups.length}.
            </p>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                view === "highlights"
                  ? "repeat(auto-fill, minmax(320px, 1fr))"
                  : "repeat(auto-fill, minmax(230px, 1fr))",
              gap: view === "highlights" ? 16 : 14,
            }}
          >
            {plat.map((s, i) =>
              view === "highlights" ? (
                <HighlightCard key={s.id} skill={s} onOpen={setSelected} />
              ) : (
                <GridCard
                  key={s.id}
                  skill={s}
                  rang={(page - 1) * PAGE_SIZE + i + 1}
                  onOpen={setSelected}
                />
              ),
            )}
          </div>
        </div>
      )}

      <Pager page={page} totalPages={totalPages} onPage={setPage} />

      <SlidePanel open={selected !== null} onClose={() => setSelected(null)} terugLabel="Terug naar de lijst">
        {selected && (
          <SkillDetail
            skill={selected}
            trends={detailData?.contributingTrends ?? []}
            trendsLoading={detailData === null}
            evidence={detailData?.skill.evidence ?? []}
            evidenceLoading={detailData === null}
            onOpenSource={setBronId}
            onClose={() => setSelected(null)}
          />
        )}
      </SlidePanel>

      {/* De bron achter een bewijskaart, over het skill-paneel heen. Zo blijf je
          waar je was: sluit je hem, dan staat de vaardigheid er nog. */}
      <SlidePanel level={2} open={bronId !== null} onClose={() => setBronId(null)} terugLabel="Terug naar de vaardigheid">
        {bron.error ? (
          <div style={{ padding: 24 }}>
            <LoadError reason={bron.error} onRetry={bron.reload} />
          </div>
        ) : bron.data ? (
          <SourceDetail
            source={bron.data.source}
            otherFromPublisher={bron.data.otherFromPublisher}
            onClose={() => setBronId(null)}
          />
        ) : (
          <div style={{ padding: 24, color: color.faint, fontSize: size.meta }}>bron laden…</div>
        )}
      </SlidePanel>
    </div>
  );
}
